import { createIntl, createIntlCache } from 'react-intl';
import { isEmpty, cloneDeep } from 'lodash-es';

import { sessionStorageService, localStorageService } from './StorageService';
import {
  FilterNames,
  OdometerTypeLabel,
  OdometerType,
  NetworkTypes,
  TradeState,
  TradeStateName
} from '../_shared/types/trade';
import {
  CacheKeys,
  Distances,
  Mileage,
  MILEAGE,
  Year,
  InputListDefinitions,
  FilterOrder,
  FilterPillsList,
  FilterType
} from '../_shared/constants/filters';
import messages from '../assets/messages';
import renderLocale from '../_shared/helpers/renderLocale';
import { IFilterCriteria } from '../redux/slices/filters';

const locale = renderLocale();
const intl = createIntl(
  { locale, messages: messages[locale] },
  createIntlCache()
);

class FilterService {
  // Returns the current active filterList
  static findActiveList(filterList, listId: string) {
    const activeList = filterList?.find(listItem => {
      return listItem.id === listId;
    });

    return activeList;
  }

  static getDefaultActiveListId(tradeState: number) {
    // use the url params

    const urlParams = new URLSearchParams(window.location.search);
    const listId = urlParams.get('listId');
    const filterLists = sessionStorageService.getItem(CacheKeys.filterList);

    if (listId && filterLists?.length > 0) {
      return this.findActiveList(filterLists, listId) ?? FilterNames.all;
    }

    // return saved activeList
    const savedActiveListId = this.getCachedTradeFilterId(tradeState);
    if (savedActiveListId) {
      return (
        this.findActiveList(filterLists, savedActiveListId) ?? FilterNames.all
      );
    }

    // return default
    return FilterNames.all;
  }

  static initializeActiveListId() {
    const { pathname } = window.location;

    const tradeState = pathname.includes(
      TradeStateName[TradeState.upcoming].toLowerCase()
    )
      ? TradeState.upcoming
      : TradeState.active;

    return this.getDefaultActiveListId(tradeState);
  }

  static initializeActiveNetorkId() {
    const networkCache = localStorageService.getItem(
      CacheKeys.selectedNetworkOption
    );

    // TODO Remove when switch user is implemented on
    // WEB5 move this logic to the clean up function
    const auth = localStorageService.getItem('auth');
    const userNetworks = auth?.user?.auctionSource?.buyingNetworks.map(
      network => network.id
    );
    let newNetworkCache = {};

    // Creates a new object with the invalid DGN selections removed
    Object.keys(networkCache ?? []).forEach(tradeState => {
      if (userNetworks && userNetworks.includes(networkCache[tradeState]))
        newNetworkCache[tradeState] = networkCache[tradeState];
    });

    newNetworkCache = this.addNetworkFromUrlParams(newNetworkCache);

    // Validate and remove invalid DGN ids from localStorage
    if (!Object.keys(newNetworkCache).length) {
      localStorageService.removeItem(CacheKeys.selectedNetworkOption);
      return null;
    }

    // Re-set localStorage with the validated network selection object
    localStorageService.setItem(
      CacheKeys.selectedNetworkOption,
      newNetworkCache
    );

    return newNetworkCache
      ? {
          [TradeState.active]: newNetworkCache[TradeState.active],
          [TradeState.upcoming]: newNetworkCache[TradeState.upcoming]
        }
      : null;
  }

  // add/update active network if url parameter "networkId" set
  static addNetworkFromUrlParams(networksSaved: object) {
    const { search, pathname } = window.location;
    const urlParams = new URLSearchParams(search);
    const networkId = urlParams.get('networkId');
    let tradeState;
    let networksUpdated;

    if (pathname.includes(TradeStateName[TradeState.active].toLowerCase())) {
      tradeState = TradeState.active;
    } else if (
      pathname.includes(TradeStateName[TradeState.upcoming].toLowerCase())
    ) {
      tradeState = TradeState.upcoming;
    }

    if (networkId && tradeState) {
      networksUpdated = { ...networksSaved, [tradeState]: networkId };
    }

    return networksUpdated || networksSaved;
  }

  static getCachedTradeFilterId(tradeState: number) {
    const listCache = localStorageService.getItem(CacheKeys.selectedFilter);

    return listCache ? listCache[tradeState] : undefined;
  }

  static setCachedTradeFilterId(tradeState: number, filterId: number) {
    const listCache =
      localStorageService.getItem(CacheKeys.selectedFilter) ?? {};
    listCache[tradeState] = filterId;

    localStorageService.setItem(CacheKeys.selectedFilter, listCache);
  }

  static isSameAddress(address1, address2) {
    return (
      address1.id === address2.id ||
      (address1.postalCode === address2.postalCode &&
        address1.street === address2.street &&
        address1.city === address2.city &&
        address1.province === address2.province &&
        address1.location.latitude === address2.location.latitude &&
        address1.location.longitude === address2.location.longitude)
    );
  }

  /**
   * Generates an address label for an address object
   */
  static generateAddressLabel(addresses) {
    if (!Array.isArray(addresses)) {
      return;
    }

    addresses.forEach(address => {
      const addressCopy = address;
      addressCopy.label = `${address.street} ${address.city}, ${address.province} ${address.postalCode}`;
    });
  }

  /**
   * evaluates which address is the default delivery address -
   * hence the default selected address in filter
   */
  static isDealerConfiguredDefaultDeliveryAddress(address, dealer) {
    return (
      this.isSameAddress(address, dealer.deliveryAddress) &&
      dealer.deliveryAddress.isTransportSettingsAddress
    );
  }

  /**
   * Map collection to object key value pair
   */
  static transformToMap(arr: Array<any>) {
    const obj = {};

    arr.forEach(value => {
      obj[value.id] = {
        key: value.id,
        displayName: value.displayName,
        isSelected: true
      };
    });

    return obj;
  }

  /**
   * Setups the selected filterlist elements based on the saved criteria list
   */
  static setupSelectedElements(criteriaList, filterList) {
    if (!criteriaList) {
      return;
    }

    if (Array.isArray(filterList)) {
      filterList.forEach(element => {
        if (criteriaList.length === 0) {
          element.isSelected = true;
        } else if (criteriaList.indexOf(element.key) !== -1) {
          element.isSelected = true;
        } else {
          element.isSelected = false;
        }
      });
    } else if (typeof filterList === 'object') {
      Object.keys(filterList).forEach(key => {
        if (criteriaList.length === 0) {
          filterList[key].isSelected = true;
        } else if (criteriaList.indexOf(Number(key)) !== -1) {
          filterList[key].isSelected = true;
        } else {
          filterList[key].isSelected = false;
        }
      });
    }
  }

  /**
   * Returns the distance values used by Distance filter
   */
  static setUpDistance(configurations) {
    let distanceConfiguration;

    if (configurations) {
      distanceConfiguration = configurations;
    } else {
      distanceConfiguration = Distances;
    }

    const distances = distanceConfiguration.map((distance, index) => {
      return {
        // translate Kilometre
        displayName: `${intl.formatMessage(
          {
            id: 'list.filters.away'
          },
          {
            distance,
            unit: OdometerTypeLabel.Kilometre
          }
        )}`,
        value: distance,
        index,
        isSelected: false
      };
    });

    // Add any distance away
    distances.push({
      displayName: intl.formatMessage({ id: 'list.filters.distance.any' }),
      value: -1,
      index: -1,
      isSelected: false
    });

    return distances;
  }

  /**
   * Setup selected distance based using metadata
   */
  static setUpSelectedDistance(criteriaList, distances) {
    const defaultDistanceIndex = distances.length - 1;

    if (criteriaList?.index === undefined) {
      distances[defaultDistanceIndex].isSelected = true;

      return;
    }

    distances.some(distance => {
      if (distance.index === criteriaList.index) {
        distance.isSelected = true;
        return true;
      }

      return false;
    });
  }

  /**
   * Set up the buyer address filter using metadata
   */
  static setupBuyerAddress(criteriaList, buyerAddresses) {
    if (!criteriaList?.dropoffAddressId) {
      const index = buyerAddresses.findIndex(
        address => address.isDefault === true
      );

      if (index !== -1) {
        return;
      }
    }

    buyerAddresses.some(address => {
      if (address?.dropoffAddressId === criteriaList?.dropoffAddressId) {
        address.isSelected = true;
        return true;
      }

      return false;
    });
  }

  /**
   * Computes the year steps
   */
  static computeYearSteps() {
    const today = new Date();
    const yearCeil = today.getFullYear() + Year.step;

    // First step is ridiculously low, to be inclusive
    const yearStepsArray = [0];

    for (let year = Year.floor; year <= yearCeil; year += Year.step) {
      yearStepsArray.push(year);
    }

    // Last step is ridiculously high, to be inclusive
    yearStepsArray.push(new Date().getFullYear() + 3);

    return yearStepsArray;
  }

  /**
   * Returns the year index
   *
   */
  static getYearIndex(year) {
    const today = new Date();
    const maxYear = today.getFullYear() + Year.step;
    const yearStepsArray = this.computeYearSteps();
    if (year > maxYear) {
      return yearStepsArray.length - 1;
    }
    return yearStepsArray.indexOf(year) !== -1
      ? yearStepsArray.indexOf(year)
      : 0;
  }

  /**
   * Computes the mileage steps
   */
  static computeMileageSteps() {
    const mileageStepsArray = [];

    for (
      let mileage = Mileage.floor;
      mileage <= Mileage.ceil;
      mileage += Mileage.step
    ) {
      mileageStepsArray.push(mileage);
    }

    // Last step is ridiculously high, to be inclusive
    mileageStepsArray.push(MILEAGE.max);

    return mileageStepsArray;
  }

  /**
   * Returns the mileage index
   */
  static getMileageIndex(mileage) {
    // Round mileage
    const roundMileage = Math.round(mileage / Mileage.step) * Mileage.step;
    const mileageStepsArray = this.computeMileageSteps();

    // Are we above the range?
    if (roundMileage > Mileage.ceil) {
      return mileageStepsArray.length - 1;
    }
    return mileageStepsArray.indexOf(mileage);
  }

  /**
   * Handles filter pill display logic
   */
  static handleFilterPillDisplay(collection, isNested: boolean) {
    const isSelected = 'isSelected';
    const nestedList = 'children';
    let isFiltered;

    if (!isEmpty(collection)) {
      if (!isNested) {
        isFiltered = Object.values(collection).some(item => !item[isSelected]);
      } else {
        isFiltered = Object.values(collection).some(parent =>
          parent[nestedList].some(item => !item[isSelected])
        );
      }
    }

    return isFiltered;
  }

  static removeFilterPill(filterName, filterState) {
    const isSelected = 'isSelected';
    const nestedList = 'children';
    let filterKey;
    let filterData;

    switch (filterName) {
      case FilterPillsList.IsInStock:
      case FilterPillsList.IsFloorPriceMet:
      case FilterPillsList.IsFranchiseDealer: {
        filterData = {
          ...filterState.tradeConditions,
          [filterName]: false
        };
        filterKey = FilterType.TradeCondition;
        break;
      }
      case FilterPillsList.OmitAsIs:
      case FilterPillsList.OmitHistory:
      case FilterPillsList.OmitAmount: {
        const hideConditions = {
          omitAsIs: '0',
          omitHistory: '1',
          omitAmount: '2'
        };
        filterData = filterState.conditions.map(condition => {
          if (condition.key === hideConditions[filterName]) {
            return {
              ...condition,
              [isSelected]: false
            };
          }
          return condition;
        });
        filterKey = FilterType.Condition;
        break;
      }
      case FilterPillsList.FuelType:
      case FilterPillsList.TransmissionType: {
        const updatedVehicleFilter = Object.values(filterState[filterName]).map(
          item => {
            if (!item[isSelected]) {
              item[isSelected] = true;
            }
            return item;
          }
        );

        filterData = { ...updatedVehicleFilter };
        filterKey = filterName;
        break;
      }
      case FilterPillsList.Province: {
        const updatedLocations = Object.values(filterState.locations).map(
          item => {
            if (!item[isSelected]) {
              item[isSelected] = true;
            }
            return item;
          }
        );

        filterData = { ...updatedLocations };
        filterKey = FilterType.Location;
        break;
      }
      case FilterPillsList.Distance: {
        const updatedDistance = filterState.distances.map(item => {
          if (item.value === -1) {
            item[isSelected] = true;
          } else {
            item[isSelected] = false;
          }
          return item;
        });

        filterData = updatedDistance;
        filterKey = FilterType.Distance;
        break;
      }
      case FilterPillsList.MileageRange: {
        filterData = { start: 0, end: MILEAGE.max };
        filterKey = FilterType.MileageRange;
        break;
      }
      case FilterPillsList.Make: {
        const updatedMakes = {};

        Object.keys(filterState.manufacturers).forEach(id => {
          updatedMakes[id] = { ...filterState.manufacturers[id] };
          if (!updatedMakes[id][isSelected]) {
            updatedMakes[id][isSelected] = true;
          }
        });

        filterData = updatedMakes;
        filterKey = FilterType.Manufacturer;
        break;
      }
      case FilterPillsList.Model: {
        const updatedModels = {};

        Object.keys(filterState.models).forEach(id => {
          updatedModels[id] = { ...filterState.models[id] };
          const modelsList = updatedModels[id][nestedList].map(model => {
            if (!model[isSelected]) {
              model[isSelected] = true;
            }
            return model;
          });
          updatedModels[id].children = modelsList;
        });

        filterData = updatedModels;
        filterKey = FilterType.Model;
        break;
      }
      default: {
        return undefined;
      }
    }
    return { filterData, filterKey };
  }

  /**
   * Returns a formatted text of elements selected in collection
   */
  static formatFilterPillName(collection, formatText, isNested: boolean) {
    let noItemsSelected = 0;
    const { selected } = formatText;

    if (Array.isArray(collection)) {
      noItemsSelected = collection.reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        noItemsSelected
      );
    } else if (typeof collection === 'object') {
      if (!isNested) {
        const arr = Object.keys(collection);
        arr.forEach(value => {
          if (collection[value].isSelected) {
            noItemsSelected += 1;
          }
        });
      } else {
        Object.keys(collection).forEach(item => {
          noItemsSelected += collection[item].children.filter(
            nestedItem => nestedItem.isSelected
          ).length;
        });
      }
    }

    let filterType;

    if (selected === 'list.filters.province') {
      filterType = intl.formatMessage(
        {
          id: selected
        },
        {
          provinceCount: noItemsSelected
        }
      );
    } else {
      filterType = intl.formatMessage({
        id: selected
      });
    }
    const numOfItemsSelected = intl.formatMessage(
      {
        id: 'list.filters.numberSelected'
      },
      {
        number: noItemsSelected
      }
    );

    return `${filterType}: ${numOfItemsSelected}`;
  }

  /**
   * Returns a formatted text of elements selected in collection
   */
  static formatItemsSelected(collection, formatText, isNested: boolean) {
    let noItems;
    let noItemsSelected = 0;
    const { all, some } = formatText;

    if (Array.isArray(collection)) {
      noItemsSelected = collection.reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        noItemsSelected
      );

      noItems = collection.length;
    } else if (typeof collection === 'object') {
      if (!isNested) {
        const arr = Object.keys(collection);
        arr.forEach(value => {
          if (collection[value].isSelected) {
            noItemsSelected += 1;
          }
        });

        noItems = arr.length;
      } else {
        Object.keys(collection).forEach(item => {
          noItems += item.length;
          noItemsSelected += collection[item].children.filter(
            nestedItem => nestedItem.isSelected
          ).length;
        });
      }
    }

    return noItems === noItemsSelected
      ? intl.formatMessage({ id: all })
      : intl.formatMessage(
          {
            id: some
          },
          { number: noItemsSelected }
        );
  }

  static getActiveItem(collection) {
    let item;

    if (Array.isArray(collection)) {
      item = collection.find(value => value.isSelected);
    } else if (typeof collection === 'object') {
      const arr = Object.keys(collection);
      const key = arr.find(value => collection[value].isSelected);

      item = collection[key];
    }

    return item?.displayName;
  }

  /**
   * Returns display name of manufacturer
   */
  static getManufacturerName(id: number, manufacturers) {
    let displayName;
    Object.keys(manufacturers).some(manufacturerId => {
      if (Number(manufacturerId) === id) {
        displayName = manufacturers[manufacturerId].displayName;
        return true;
      }

      return false;
    });

    return displayName;
  }

  /**
   * Returns selected models in models list
   */
  static getSelectedModels(models, manufacturers) {
    const selectedModels = {};

    if (isEmpty(models)) {
      return selectedModels;
    }

    const selectedManufacturerId = Object.keys(manufacturers).filter(
      manufacturerId => manufacturers[manufacturerId].isSelected
    );

    selectedManufacturerId.forEach(manufacturerId => {
      if (!models[manufacturerId]) {
        return;
      }

      selectedModels[manufacturerId] = models[manufacturerId].children.filter(
        child => child.isSelected
      );
    });

    return selectedModels;
  }

  /**
   * Helper function to check if all manufacturers selected
   */
  static isAllSelected(manufacturers) {
    return Object.keys(manufacturers).every(
      k => manufacturers[k].isSelected === true
    );
  }

  /**
   * Create list criteria for preview requests
   */
  static createListPreviewRequest(
    filterState,
    criteria,
    key: string
  ): IFilterCriteria {
    const updatedCriteria = { ...filterState };
    if (key) {
      updatedCriteria[key] = criteria;
    }

    const modelTrims = [];

    const { models, manufacturers } = updatedCriteria;

    if (!this.isAllSelected(manufacturers)) {
      // Add models
      if (!isEmpty(models)) {
        Object.keys(models).forEach(id => {
          models[id].children.forEach(model => {
            if (model.isSelected) {
              modelTrims.push({
                model: {
                  displayName: model.displayName,
                  manufacturerId: Number(id)
                }
              });
            }
          });
        });
      }

      // Add manufacturers
      Object.keys(manufacturers).forEach(manufacturerId => {
        if (
          manufacturers[manufacturerId].isSelected &&
          !modelTrims.some(
            trim => trim.model.manufacturerId === parseInt(manufacturerId, 10)
          )
        ) {
          modelTrims.push({
            model: {
              manufacturerId: Number(manufacturerId)
            }
          });
        }
      });
    }

    const inputDefinitions = [];
    const locationFilter = {} as any;

    const { showDistance } = updatedCriteria;

    if (!showDistance) {
      // locations
      const { locations } = updatedCriteria;
      Object.keys(locations).forEach(locationId => {
        if (locations[locationId].isSelected) {
          inputDefinitions.push({
            key: InputListDefinitions.locations.toString(),
            value: locations[locationId].key
          });
        }
      });
    }
    // distances
    else if (showDistance && updatedCriteria.distances.length > 0) {
      const selectedDistances = updatedCriteria.distances.find(
        distance => distance.isSelected
      );
      locationFilter.distance = selectedDistances.value;
      locationFilter.index = selectedDistances.index;

      const selectedBuyerAddress = updatedCriteria.buyerAddresses.find(
        address => address.isSelected || address.isDefault
      );
      locationFilter.dropoffAddressId = selectedBuyerAddress?.dropoffAddressId;
      locationFilter.location = {
        latitude: selectedBuyerAddress?.latitude,
        longitude: selectedBuyerAddress?.longitude
      };
      locationFilter.postalCode = selectedBuyerAddress?.postalCode;
      locationFilter.odometerType = OdometerType.Kilometre;
    }

    // Add conditions
    updatedCriteria.conditions.forEach(condition => {
      if (condition.isSelected) {
        inputDefinitions.push({
          key: InputListDefinitions.conditions.toString(),
          value: condition.key
        });
      }
    });

    // Add fuelTypes
    const fuelTypeIds = [];
    const { fuelTypes } = updatedCriteria;
    Object.keys(fuelTypes).forEach(fuelTypeId => {
      if (fuelTypes[fuelTypeId].isSelected) {
        fuelTypeIds.push({ id: Number(fuelTypeId) });
      }
    });

    // Add transmission types
    const { transmissionTypes } = updatedCriteria;
    const transmissionTypesId = Object.keys(transmissionTypes)
      .filter(typeId => transmissionTypes[typeId].isSelected)
      .map(typeId => {
        return { id: transmissionTypes[typeId].key };
      });

    const {
      isInStock,
      isFloorPriceMet,
      isFranchiseDealer
    } = updatedCriteria.tradeConditions;

    const listRequest = {
      criteria: {
        modelYear: {
          start: updatedCriteria.yearRange.start,
          end: updatedCriteria.yearRange.end
        },
        mileage: {
          start: updatedCriteria.mileageRange.start,
          end: updatedCriteria.mileageRange.end,
          odometerType: OdometerType.Kilometre
        },
        modelTrims,
        inputDefinitions,
        fuelTypes: fuelTypeIds,
        transmissionTypes: transmissionTypesId,
        isFranchiseDealer,
        isInStock,
        isFloorPriceMet,
        locationFilter
      }
    };

    return listRequest;
  }

  static async loadModels(
    fromYear: number,
    toYear: number,
    manufacturers,
    criteriaModels = {},
    getModelList: (args: object) => Promise<Array<any>>
  ) {
    const manufacturerIds = [];

    if (!this.isAllSelected(manufacturers)) {
      Object.keys(manufacturers).forEach(id => {
        if (manufacturers[id].isSelected) {
          manufacturerIds.push(Number(id));
        }
      });
    }

    const modelList = await getModelList({
      fromYear,
      toYear,
      manufacturerIds
    });

    const allModels = {};
    const groupedModels = [];

    // Group the models by manufactuerId
    manufacturerIds.forEach(id => {
      const models = modelList.filter(list => list.manufacturerId === id);

      if (models.length > 0) {
        groupedModels.push({
          models,
          manufacturerId: id
        });
      }
    });

    groupedModels.forEach(groupModel => {
      let selectedModels = [];

      Object.keys(criteriaModels).forEach(manufacturerId => {
        if (parseInt(manufacturerId, 10) === groupModel.manufacturerId) {
          selectedModels = criteriaModels[manufacturerId];
        }
      });

      groupModel.models.forEach(model => {
        if (selectedModels.length) {
          model.isSelected = selectedModels.some(
            selectedModel => selectedModel.displayName === model.displayName
          );
        } else {
          model.isSelected = true;
        }

        allModels[groupModel.manufacturerId] = {
          children: groupModel.models,
          displayName: this.getManufacturerName(
            groupModel.manufacturerId,
            manufacturers
          ),
          isSelected: true
        };
      });
    });

    return allModels;
  }

  /**
   * returns list of selectable models with previous model list selection applied
   *
   * @param {object} modelsSelectable new list of possible models
   * @param {object} modelsSelected old list holding selection info
   * @returns {object}
   */
  static applyModelsSelection = (modelsSelectable, modelsSelected) => {
    const models = {};
    Object.keys(modelsSelectable).forEach(key => {
      if (
        !modelsSelected[key]?.children ||
        modelsSelected[key]?.children.every(model => model.isSelected === true)
      ) {
        models[key] = modelsSelectable[key]; // all models selected for the make
      } else {
        const make = {
          ...modelsSelectable[key],
          children: modelsSelectable[key].children.map(model => {
            const modelSaved = modelsSelected[key].children.find(item => {
              return item.displayName === model.displayName;
            });
            return {
              ...model,
              isSelected: modelSaved ? modelSaved.isSelected : false
            };
          })
        };
        models[key] = make.children.every(model => model.isSelected === false)
          ? modelsSelectable[key]
          : make;
      }
    });
    return models;
  };

  static createBuyerAddress = (address, dealer) => {
    return {
      displayName: `${intl.formatMessage({ id: 'list.filters.from' })} ${
        address.street
      } ${address.city}, ${address.province} ${address.postalCode}`,
      dropoffAddressId: address.id,
      latitude: address.location.latitude,
      longitude: address.location.longitude,
      postalCode: address.postalCode,
      isSelected: FilterService.isDealerConfiguredDefaultDeliveryAddress(
        address,
        dealer
      )
    };
  };

  /**
   * Returns filter metadata used to populate filters
   */
  static loadMetaData = (
    listMetaData,
    manufacturersList,
    dealerDetails,
    tradeMetaData,
    distanceConfigurations
  ) => {
    const locationDefinition = listMetaData.inputListDefinitions.find(
      metaData => {
        return metaData.id === InputListDefinitions.locations;
      }
    ).inputs;

    const locations = {};

    locationDefinition.forEach(_location => {
      _location.displayName = _location.value;
      _location.isSelected = true;
      locations[_location.key] = { ..._location };
    });

    const conditionDefinition = listMetaData.inputListDefinitions.find(
      metaData => {
        return metaData.id === InputListDefinitions.conditions;
      }
    ).inputs;

    const conditions = conditionDefinition.map(condition => {
      condition.displayName = condition.value;
      return condition;
    });

    const manufacturers = {};
    const cloneManufacutersList = cloneDeep(manufacturersList);

    cloneManufacutersList.forEach(element => {
      if (element.isVisible === true) {
        manufacturers[element.id] = element;
      }
    });

    const dealer = dealerDetails;

    const buyerAddresses = dealer.otherAddresses.map(address => {
      return FilterService.createBuyerAddress(address, dealer);
    });

    // if delivery address is not contained in otherAddresses,
    // then add it to otherAddresses just for the filter
    if (dealer.deliveryAddress) {
      if (
        !dealer.otherAddresses.find(address =>
          FilterService.isSameAddress(address, dealer.deliveryAddress)
        )
      ) {
        buyerAddresses.push(
          FilterService.createBuyerAddress(dealer.deliveryAddress, dealer)
        );
      }
    }

    // if no default delivery address was detected in the step immediately above, then set the legacy default
    if (!buyerAddresses.find(address => address.isSelected === true)) {
      const legacyDefaultAddress = dealer.otherAddresses.find(
        address => address.isSelected === true
      );
      if (legacyDefaultAddress?.id) {
        buyerAddresses.find(
          address => address?.dropoffAddressId === legacyDefaultAddress.id
        ).isSelected = true;
      }
    }

    // Fuel Types
    const fuelTypes = FilterService.transformToMap(tradeMetaData.fuelTypes);

    // Transmissiontype
    const transmissionTypes = FilterService.transformToMap(
      tradeMetaData.transmissions
    );

    const distances = FilterService.setUpDistance(distanceConfigurations);

    return {
      locations,
      conditions,
      manufacturers,
      buyerAddresses,
      fuelTypes,
      transmissionTypes,
      distances
    };
  };

  /**
   * Return populated filter criteria using filter metadata
   */
  static async populateFilters(
    metaData,
    filterCriteria: IFilterCriteria,
    getModelList: (args) => Promise<Array<any>>,
    tradeState: number,
    setFilterState: (state: {}) => void,
    dispatchFilterMetaData: (metaData: {}) => void
  ) {
    const {
      locations,
      conditions,
      manufacturers,
      buyerAddresses,
      fuelTypes,
      transmissionTypes,
      distances
    } = metaData;

    const { criteria } = filterCriteria;

    const selectedModels = {};

    const updatedManufacturers = { ...manufacturers };
    let updatedModels = {};

    // Parse Model trims
    if (criteria?.modelTrims?.length) {
      criteria.modelTrims.forEach(modelTrim => {
        let manufacturer = {} as any;
        Object.keys(updatedManufacturers).some(element => {
          if (
            updatedManufacturers[element].id === modelTrim.model.manufacturerId
          ) {
            manufacturer = updatedManufacturers[element];
            return true;
          }

          return false;
        });

        if (!modelTrim?.model?.displayName) {
          // Manufacturer only
          manufacturer.isSelected = true;
        } else if (manufacturer) {
          manufacturer.isSelected = true;

          // Manufactuer and model
          if (!Array.isArray(selectedModels[manufacturer.id])) {
            selectedModels[manufacturer.id] = [];
          }

          selectedModels[manufacturer.id].push(modelTrim.model);
        }
      });
    } else {
      Object.keys(updatedManufacturers).forEach(manufacturer => {
        updatedManufacturers[manufacturer].isSelected = true;
      });
    }

    if (Object.keys(selectedModels).length) {
      const { start, end } = criteria.modelYear;

      updatedModels = await this.loadModels(
        start,
        end,
        updatedManufacturers,
        selectedModels,
        getModelList
      );
    }

    // Locations
    const updatedLocations = { ...locations };

    const locationCriteria = criteria?.inputDefinitions?.filter(
      inputDefinition =>
        inputDefinition.key === InputListDefinitions.locations.toString()
    );

    const locationKeys = locationCriteria.map(_location => _location.value);

    // setupSelectedElements(locationKeys, Object.entries(updatedLocations));
    if (locationKeys) {
      Object.keys(updatedLocations).forEach(key => {
        if (locationKeys.length === 0) {
          updatedLocations[key].isSelected = true;
        } else if (locationKeys.indexOf(key) !== -1) {
          updatedLocations[key].isSelected = true;
        } else {
          updatedLocations[key].isSelected = false;
        }
      });
    }

    // Fuel Types
    const updatedFuelTypes = { ...fuelTypes };

    let fuelTypesCriteria;

    if (criteria.fuelTypes) {
      fuelTypesCriteria = criteria.fuelTypes.map(fuelType => fuelType.id);
    }

    this.setupSelectedElements(fuelTypesCriteria, updatedFuelTypes);

    // Transmission types
    const updatedTransmissionTypes = { ...transmissionTypes };

    let transmissionTypeCriteria;

    // TransmissionTypes
    if (criteria.transmissionTypes) {
      transmissionTypeCriteria = criteria.transmissionTypes.map(
        transmissionType => {
          return transmissionType.id;
        }
      );
    }

    this.setupSelectedElements(
      transmissionTypeCriteria,
      updatedTransmissionTypes
    );

    // Setup distance and location
    const updatedDistance = cloneDeep(distances);

    this.setUpSelectedDistance(criteria.locationFilter, updatedDistance);

    // Buyer addresss
    const updatedBuyerAddress = cloneDeep(buyerAddresses);
    this.setupBuyerAddress(criteria.locationFilter, updatedBuyerAddress);

    // Conditions
    const updatedConditions = cloneDeep(conditions);
    const conditionCriteria = criteria?.inputDefinitions?.filter(
      inputDefinition =>
        inputDefinition.key === InputListDefinitions.conditions.toString()
    );

    const conditionKeys = conditionCriteria.map(condition => condition.value);

    updatedConditions.forEach(condition => {
      condition.isSelected = false;

      if (conditionKeys.indexOf(condition.key) !== -1) {
        condition.isSelected = true;
      }
    });

    // Year
    const updatedYearSlider = {
      start: criteria.modelYear.start,
      end: criteria.modelYear.end
    };

    // Mileage
    const updatedMileageSlider = {
      start: criteria.mileage.start,
      end: criteria.mileage.end
    };

    // Trade conditions
    const tradeConditions = {
      isFranchiseDealer: !!criteria.isFranchiseDealer,
      isInStock: criteria.isInStock
    } as any;

    if (tradeState !== TradeState.upcoming) {
      tradeConditions.isFloorPriceMet = criteria.isFloorPriceMet;
    }

    // showDistance
    let showDistance = false;
    if (
      criteria?.locationFilter &&
      Object.keys(criteria?.locationFilter)?.length
    ) {
      showDistance = true;
    }

    const newFilterState = {
      locations: updatedLocations,
      conditions: updatedConditions,
      manufacturers: updatedManufacturers,
      models: updatedModels,
      buyerAddresses: updatedBuyerAddress,
      distances: updatedDistance,
      fuelTypes: updatedFuelTypes,
      transmissionTypes: updatedTransmissionTypes,
      yearRange: updatedYearSlider,
      mileageRange: updatedMileageSlider,
      tradeConditions,
      showDistance
    };

    setFilterState(newFilterState);
    dispatchFilterMetaData(newFilterState);
  }

  /**
   * Return if the filter type indicates a DGN filter
   */
  static isDGNFilter(type: number) {
    return type === NetworkTypes.buyerNetwork.type;
  }

  /**
   * Remove DGN Filters
   */
  static removeDGNFilters(filterList) {
    if (!filterList) {
      return [];
    }
    const updatedFilterList = filterList.filter(
      list => !this.isDGNFilter(list.type)
    );

    return updatedFilterList;
  }

  /**
   * Returns the currently selected items in the filter list
   */
  static getSelectedItemsList(list) {
    let displayNames = [];
    if (Array.isArray(list)) {
      displayNames = list
        .filter(item => item.isSelected || item.isDefault)
        .map(item => item.displayName);
    } else {
      displayNames = Object.keys(list)
        .filter(itemId => list[itemId].isSelected || list[itemId].isDefault)
        .map(itemId => list[itemId].displayName);
    }

    return displayNames;
  }

  /**
   * Returns the currently selected items in the models list
   */
  static getSelectedItemsModels(list) {
    const displayNames = {};

    Object.keys(list).forEach(itemId => {
      const name = list[itemId]?.displayName;
      displayNames[name] = list[itemId].children
        .filter(child => child.isSelected)
        .map(child => child.displayName);
    });

    return displayNames;
  }

  /**
   * Returns if the filterName already exists in the list of saved filters
   */
  static isDuplicateFilterName(filterList, filterName: string) {
    const formattedName = filterName?.toLocaleLowerCase().trim();

    return filterList.find(
      item => item.title.toLocaleLowerCase().trim() === formattedName
    );
  }

  /**
   * Sorts the filterlist based on order set in filter constants
   */
  static sortUserFilterList(filterList) {
    if (!Array.isArray(filterList)) {
      return;
    }

    filterList.sort((a, b) => {
      return FilterOrder.indexOf(a.type) - FilterOrder.indexOf(b.type);
    });
  }
}

export default FilterService;
